import { AppShell, PageTitle } from "@/components/app-shell";
import { PresenceRail } from "@/components/presence-rail";
import { ReviewListSkeleton } from "@/components/mission-skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getHardware, type HardwareRevision } from "@/lib/api";
import {
  changeHref,
  changeRowMeta,
  changeSnapshotOptions,
  countOpenChanges,
  defaultSnapshotPair,
  isOpenChangeStatus,
  jobsGateAriaLabel,
  jobsGateDotClass,
  newChangeBody,
  type ChangeSnapshotOption,
  type JobsGateDot,
} from "@/lib/changes-list";
import {
  COLLAB_IA,
  canWriteCollaboration,
  createChange,
  listChanges,
  type ChangeListItem,
} from "@/lib/collaboration";
import { useProjectScope } from "@/lib/project-scope";
import { scopeLoadKey, useKeyedLoad } from "@/lib/scope-loading";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

export function ChangesListPage() {
  const { orgSlug, projectSlug } = useParams();
  const { role } = useProjectScope();
  const [params] = useSearchParams();
  const statusFilter = params.get("filter") || "all";
  const [items, setItems] = useState<ChangeListItem[]>([]);
  const [revisions, setRevisions] = useState<HardwareRevision[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const canWrite = canWriteCollaboration(role);
  const routeKey = scopeLoadKey(orgSlug, projectSlug);
  const { loading, start, finish } = useKeyedLoad(routeKey);
  const nav = useNavigate();

  useEffect(() => {
    setItems([]);
    setRevisions([]);
    setErr(null);
    setSelectedId(null);
    setCreateOpen(false);
  }, [routeKey]);

  const load = useCallback(async () => {
    const isCurrent = start();
    if (!orgSlug || !projectSlug) {
      finish(isCurrent);
      return;
    }
    try {
      const [body, hw] = await Promise.all([listChanges(orgSlug, projectSlug), getHardware(orgSlug, projectSlug)]);
      if (!isCurrent()) return;
      setItems(body.items ?? []);
      setRevisions(hw.revisions ?? []);
      setErr(null);
    } catch (e) {
      if (!isCurrent()) return;
      setItems([]);
      setErr(e instanceof Error ? e.message : "changes load failed");
    } finally {
      finish(isCurrent);
    }
  }, [orgSlug, projectSlug, start, finish]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (canWrite && params.get("new") === "1") setCreateOpen(true);
  }, [canWrite, params]);

  const org = orgSlug || "";
  const proj = projectSlug || "";
  const openCount = countOpenChanges(items);
  const snapshots = useMemo(() => changeSnapshotOptions(revisions), [revisions]);

  return (
    <AppShell
      title={
        <PageTitle
          kicker={COLLAB_IA.changesKicker}
          extra={
            canWrite && !loading ? (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setCreateOpen(true)}
                data-testid="new-change"
                data-mission-id="action:new-change"
                data-mission-panel="new-review"
              >
                New review
              </Button>
            ) : null
          }
        >
          Changes
        </PageTitle>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="changes-ia">
        <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
          {openCount} open
        </span>
        <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
          Jobs · deterministic
        </span>
        <span
          className="rounded-full border border-dashed border-white/25 px-2 py-0.5 font-mono text-[10px] tracking-[0.04em] text-muted-foreground"
          data-testid="presence-v11"
        >
          Presence V1.1
        </span>
        <PresenceRail orgSlug={orgSlug} projectSlug={projectSlug} surface="changes" />
      </div>
      <p className="mb-3 text-[11px] text-muted-foreground">
        {COLLAB_IA.checksHint} {COLLAB_IA.noMerge}
      </p>
      {err && <p className="mb-3 text-sm text-destructive">{err}</p>}
      {loading ? (
        <ReviewListSkeleton />
      ) : items.length === 0 ? (
        err ? null : (
          <p className="text-sm text-muted-foreground">
            No changes yet. A change is a baseline snapshot ↔ candidate snapshot — Review is a stage on that change.
          </p>
        )
      ) : (
        <ChangesTable
          items={
            statusFilter === "all"
              ? items
              : statusFilter === "open"
                ? items.filter((item) => isOpenChangeStatus(item.review_status))
                : items.filter((item) => !isOpenChangeStatus(item.review_status))
          }
          org={org}
          proj={proj}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      )}
      <NewChangeDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        orgSlug={orgSlug}
        projectSlug={projectSlug}
        snapshots={snapshots}
        snapshotsReady={!loading}
        canWrite={canWrite}
        onCreated={(created) => {
          setCreateOpen(false);
          nav(changeHref(org, proj, created));
        }}
        onError={setErr}
      />
    </AppShell>
  );
}

export function ChangesTable({
  items,
  org,
  proj,
  selectedId,
  onSelect,
}: {
  items: ChangeListItem[];
  org: string;
  proj: string;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-[4px] border border-border bg-[#0C0C0C]" data-testid="changes-list">
      <div className="grid min-w-[640px] grid-cols-[minmax(0,1fr)_7.5rem_8.5rem_6.5rem] gap-3 border-b border-border px-3 py-2 text-[10px] font-medium tracking-[0.08em] text-muted-foreground uppercase">
        <div>Review</div>
        <div>Status</div>
        <div>Jobs gate</div>
        <div className="text-right">Author</div>
      </div>
      <ul className="m-0 min-w-[640px] list-none p-0">
        {items.map((item) => (
          <ChangeRow
            key={item.id}
            item={item}
            href={changeHref(org, proj, item)}
            selected={selectedId === item.id}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </div>
  );
}

export function ChangeRow({
  item,
  href,
  selected,
  onSelect,
}: {
  item: ChangeListItem;
  href: string;
  selected?: boolean;
  onSelect?: (id: string) => void;
}) {
  const meta = changeRowMeta(item);
  return (
    <li>
      <Link
        to={href}
        data-testid="change-row"
        data-mission-id={`change:${item.id}`}
        data-change-id={item.id}
        data-selected={selected ? "true" : "false"}
        onClick={() => onSelect?.(item.id)}
        className={cn(
          "grid grid-cols-[minmax(0,1fr)_7.5rem_8.5rem_6.5rem] items-center gap-3 border-l-2 border-transparent px-3 py-2.5 no-underline",
          "hover:bg-[#141414]",
          selected && "border-l-[var(--copper)] bg-[#141414]",
        )}
      >
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium text-foreground">{meta.title}</div>
          <div className="truncate font-mono text-[11px] text-muted-foreground">{meta.oidRange}</div>
        </div>
        <div>
          <Badge variant="outline" className={meta.statusClass} data-testid="change-status">
            {meta.statusLabel}
          </Badge>
        </div>
        <JobsGateDots dots={meta.dots} label={meta.checksLabel} />
        <div className="text-right">
          <div className="truncate text-[12px] text-foreground">{meta.author}</div>
          <div className="font-mono text-[11px] text-muted-foreground">{meta.when}</div>
        </div>
      </Link>
    </li>
  );
}

export function JobsGateDots({
  dots,
  label,
}: {
  dots: JobsGateDot[];
  label: string;
}) {
  return (
    <div
      className="flex items-center gap-2"
      data-testid="jobs-gate-dots"
      title={jobsGateAriaLabel(dots, label)}
      role="img"
      aria-label={jobsGateAriaLabel(dots, label)}
    >
      {dots.map((dot) => (
        <span key={dot.kind} className="flex flex-col items-center gap-1">
          <span
            className={cn("size-1.5 rounded-full", jobsGateDotClass(dot.state))}
            data-kind={dot.kind}
            data-state={dot.state}
          />
          <span className="font-mono text-[9px] tracking-[0.04em] text-muted-foreground uppercase">{dot.kind}</span>
        </span>
      ))}
    </div>
  );
}

export function NewChangeDialog({
  open,
  onOpenChange,
  orgSlug,
  projectSlug,
  snapshots,
  snapshotsReady,
  canWrite,
  onCreated,
  onError,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgSlug?: string;
  projectSlug?: string;
  snapshots: ChangeSnapshotOption[];
  snapshotsReady: boolean;
  canWrite: boolean;
  onCreated: (created: ChangeListItem) => void;
  onError: (message: string) => void;
}) {
  const [baseline, setBaseline] = useState("");
  const [candidate, setCandidate] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLocalErr(null);
    const pair = defaultSnapshotPair(snapshots);
    setBaseline(pair.baseline);
    setCandidate(pair.candidate);
  }, [open, snapshots]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canWrite || !orgSlug || !projectSlug) return;
    const base = snapshots.find((s) => s.snapshot === baseline);
    const cand = snapshots.find((s) => s.snapshot === candidate);
    if (!base || !cand) {
      setLocalErr("Select baseline and candidate snapshots from ProjectHosting.");
      return;
    }
    setBusy(true);
    setLocalErr(null);
    try {
      const created = await createChange(orgSlug, projectSlug, newChangeBody({ baseline: base, candidate: cand, title }));
      setTitle("");
      onCreated(created);
    } catch (ex) {
      const message = ex instanceof Error ? ex.message : "create failed";
      setLocalErr(message);
      onError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-change-dialog">
        <DialogHeader>
          <DialogTitle>New review</DialogTitle>
          <DialogDescription>
            Baseline snapshot ↔ candidate snapshot. POST collaboration/changes with request_review. Does not write
            project source files. Empty findings are not a pass.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={onSubmit}>
          {!snapshotsReady ? (
            <p className="text-sm text-muted-foreground">Loading ProjectHosting snapshots…</p>
          ) : snapshots.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="new-change-empty-snapshots">
              No ProjectHosting snapshots yet. Import a hardware revision before opening a review.
            </p>
          ) : (
            <>
              <SnapshotField
                id="baseline_snapshot"
                label="baseline snapshot"
                value={baseline}
                onChange={setBaseline}
                snapshots={snapshots}
              />
              <SnapshotField
                id="candidate_snapshot"
                label="candidate snapshot"
                value={candidate}
                onChange={setCandidate}
                snapshots={snapshots}
              />
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="change-title">title (optional)</Label>
            <Input id="change-title" name="title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          {localErr && (
            <p className="text-sm text-destructive" role="alert">
              {localErr}
            </p>
          )}
          <DialogFooter>
            <Button
              type="submit"
              variant="destructive"
              disabled={!canWrite || busy || !snapshotsReady || snapshots.length === 0}
              data-testid="new-change-submit"
              data-mission-danger=""
            >
              New review
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SnapshotField({
  id,
  label,
  value,
  onChange,
  snapshots,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  snapshots: ChangeSnapshotOption[];
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full font-mono" data-testid={id}>
          <SelectValue placeholder="Select snapshot" />
        </SelectTrigger>
        <SelectContent>
          {snapshots.map((snap) => (
            <SelectItem key={snap.snapshot} value={snap.snapshot} className="font-mono">
              {snap.label}
              {snap.oid ? "" : " · no oid"}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
